import React, { useState } from 'react';
import { motion } from 'framer-motion';

const ContactUs = () => {
  const [contact, setContact] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    priority: 'normal',
    category: 'general'
  });

  const [submitted, setSubmitted] = useState(false);

  const contactCategories = [
    { value: 'general', label: '💬 General Inquiry', description: 'General questions about Dharma Guide' },
    { value: 'technical', label: '🔧 Technical Support', description: 'Website issues or technical problems' },
    { value: 'content', label: '📚 Content Questions', description: 'About scriptures or spiritual content' },
    { value: 'partnership', label: '🤝 Partnership', description: 'Collaboration opportunities' },
    { value: 'media', label: '📰 Media Inquiry', description: 'Press and media requests' },
    { value: 'other', label: '❓ Other', description: 'Something else' }
  ];

  const teamMembers = [
    {
      name: 'Spiritual Guidance Team',
      role: 'Scripture & Content',
      email: 'guidance@dharmaguide.com',
      description: 'Questions about scriptural interpretations and spiritual guidance',
      icon: '📖'
    },
    {
      name: 'Technical Support',
      role: 'Platform & Features',
      email: 'support@dharmaguide.com',
      description: 'Technical issues and platform feature requests',
      icon: '🔧'
    },
    {
      name: 'Community Team',
      role: 'Forum & Engagement',
      email: 'community@dharmaguide.com',
      description: 'Forum questions and community engagement',
      icon: '👥'
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    // Here you would typically send to your backend
    console.log('Contact form submitted:', contact);
    setSubmitted(true);
  };

  const handleReset = () => {
    setContact({
      name: '',
      email: '',
      subject: '',
      message: '',
      priority: 'normal',
      category: 'general'
    });
    setSubmitted(false);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center justify-center w-32 h-32 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full mb-8 shadow-2xl">
              <span className="text-4xl">✉️</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-6">
              Message Sent!
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Thank you for reaching out. We've received your message and will get back to you within 24-48 hours.
            </p>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700 max-w-md mx-auto">
              <div className="text-6xl mb-4">⏳</div>
              <p className="text-gray-300 mb-4">
                <strong>What to expect:</strong>
              </p>
              <ul className="text-gray-300 text-sm space-y-2 mb-6 text-left">
                <li>• Response within 24-48 hours</li>
                <li>• Personalized attention to your inquiry</li>
                <li>• Follow-up if more information is needed</li>
              </ul>
              <button
                onClick={handleReset}
                className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-8 py-3 rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
              >
                Send Another Message
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-blue-900 to-purple-900 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl mb-6 shadow-2xl">
            <span className="text-2xl">📞</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-4">
            Contact Us
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Get in touch with our team. We're here to help you on your spiritual journey and make Dharma Guide better for everyone.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700"
            >
              <h2 className="text-2xl font-bold text-white mb-6">Send us a Message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={contact.name}
                      onChange={(e) => setContact({...contact, name: e.target.value})}
                      placeholder="Enter your full name"
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      required
                      value={contact.email}
                      onChange={(e) => setContact({...contact, email: e.target.value})}
                      placeholder="your@email.com"
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Category
                    </label>
                    <select
                      value={contact.category}
                      onChange={(e) => setContact({...contact, category: e.target.value})}
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300"
                    >
                      {contactCategories.map((cat) => (
                        <option key={cat.value} value={cat.value}>
                          {cat.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      Priority
                    </label>
                    <select
                      value={contact.priority}
                      onChange={(e) => setContact({...contact, priority: e.target.value})}
                      className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300"
                    >
                      <option value="low">💚 Low Priority</option>
                      <option value="normal">💛 Normal Priority</option>
                      <option value="high">❤️ High Priority</option>
                      <option value="urgent">🚨 Urgent</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Subject *
                  </label>
                  <input
                    type="text"
                    required
                    value={contact.subject}
                    onChange={(e) => setContact({...contact, subject: e.target.value})}
                    placeholder="Brief summary of your inquiry..."
                    className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-300 mb-2">
                    Your Message *
                  </label>
                  <textarea
                    required
                    rows={8}
                    value={contact.message}
                    onChange={(e) => setContact({...contact, message: e.target.value})}
                    placeholder="Please provide detailed information about your inquiry, question, or concern..."
                    className="w-full bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-300 resize-vertical"
                  />
                </div>

                <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="text-blue-400 text-lg">💡</div>
                    <div className="text-sm">
                      <h4 className="font-semibold text-blue-400 mb-1">For faster help:</h4>
                      <p className="text-blue-300">
                        Include relevant details like specific scripture references, platform features, 
                        or steps to reproduce any issues you're experiencing.
                      </p>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white py-4 rounded-xl font-semibold text-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
                >
                  Send Message
                </button>
              </form>
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700"
            >
              <h3 className="text-lg font-bold text-white mb-4">Get in Touch</h3>
              <div className="space-y-4">
                {[
                  { icon: '📧', label: 'Email', value: 'dharmaanswers@gmail.com', desc: 'General inquiries' },
                  { icon: '🕒', label: 'Response Time', value: '24-48 hours', desc: 'Typically within' },
                  // { icon: '🌐', label: 'Office Hours', value: 'Mon - Fri, 9AM - 6PM IST', desc: 'Indian Standard Time' },
                  { icon: '🔗', label: 'Follow Us', value: '@dharmaanswers', desc: 'Social media updates' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4 p-3 rounded-lg bg-gray-700/30">
                    <div className="text-2xl">{item.icon}</div>
                    <div className="flex-1">
                      <div className="font-semibold text-white">{item.label}</div>
                      <div className="text-sm text-gray-300">{item.value}</div>
                      <div className="text-xs text-gray-400">{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Team Contacts */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-2xl p-6 border border-purple-500/20"
            >
              <h3 className="text-lg font-bold text-white mb-4">Direct Contacts</h3>
              <div className="space-y-4">
                {teamMembers.map((member, index) => (
                  <div key={index} className="p-4 rounded-lg bg-gray-700/30">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="text-2xl">{member.icon}</div>
                      <div>
                        <div className="font-semibold text-white">{member.name}</div>
                        <div className="text-sm text-purple-400">{member.role}</div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-300 mb-2">{member.description}</div>
                    <div className="text-xs text-gray-400 break-all">{member.email}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* FAQ Quick Links */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.7 }}
              className="bg-gradient-to-br from-green-500/10 to-teal-500/10 rounded-2xl p-6 border border-green-500/20"
            >
              <h3 className="text-lg font-bold text-white mb-3">Quick Help</h3>
              <p className="text-sm text-gray-300 mb-4">
                Common questions answered instantly:
              </p>
              <div className="space-y-2">
                {[
                  'How to search scriptures effectively?',
                  'Can I request specific scripture explanations?',
                  'How does the forum moderation work?',
                  'Are there mobile apps available?',
                  'How to report inappropriate content?'
                ].map((question, index) => (
                  <button
                    key={index}
                    className="w-full text-left p-3 rounded-lg bg-gray-700/30 hover:bg-gray-600/30 text-sm text-gray-300 transition-all duration-200 flex items-center gap-2"
                  >
                    <span>❓</span>
                    <span className="flex-1">{question}</span>
                  </button>
                ))}
              </div>
            </motion.div>

            {/* Emergency Contact */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="bg-gradient-to-br from-red-500/10 to-orange-500/10 rounded-2xl p-6 border border-red-500/20"
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="text-2xl">🚨</div>
                <h3 className="text-lg font-bold text-white">Urgent Spiritual Crisis</h3>
              </div>
              <p className="text-sm text-gray-300 mb-4">
                If you're experiencing a spiritual or emotional crisis requiring immediate attention:
              </p>
              <div className="text-center">
                <button className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-xl font-semibold text-sm transition-all duration-300 w-full">
                  Get Immediate Help
                </button>
                <p className="text-xs text-gray-400 mt-2">
                  Connects you with certified spiritual counselors
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;